import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import { COLORS } from '../lib/theme'
import { Icon } from './Icon'

/** Optional inline action rendered alongside the message (e.g. Undo). */
export interface ToastAction {
  label: string
  onAction: () => void
}

export interface ToastApi {
  /**
   * Show a transient success message (auto-dismisses; click to dismiss). Pass
   * an `action` to offer a single inline affordance — e.g. Undo — which runs
   * its handler and dismisses the toast.
   */
  showToast: (message: string, action?: ToastAction) => void
}

// Default is a no-op so `useToast()` is safe to call even when no provider is
// mounted (tests, prototype) — it simply does nothing rather than throwing.
const ToastContext = createContext<ToastApi>({ showToast: () => {} })

export function useToast(): ToastApi {
  return useContext(ToastContext)
}

interface ToastItem {
  id: number
  message: string
  action?: ToastAction
}

/**
 * App-level success-toast host. Mount it above the router so a toast fired
 * right before a route change (e.g. "Invitations sent" → navigate to /hunts)
 * survives the navigation. Toasts are non-blocking, auto-dismiss after
 * `duration` ms, and are dismissible by clicking them.
 */
export function ToastProvider({ children, duration = 3500 }: { children: ReactNode; duration?: number }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const idRef = useRef(0)

  const dismiss = useCallback((id: number) => {
    setToasts((ts) => ts.filter((t) => t.id !== id))
  }, [])

  const showToast = useCallback(
    (message: string, action?: ToastAction) => {
      const id = (idRef.current += 1)
      setToasts((ts) => [...ts, { id, message, action }])
      setTimeout(() => dismiss(id), duration)
    },
    [dismiss, duration],
  )

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div
        style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
          zIndex: 2000, pointerEvents: 'none',
        }}
      >
        {toasts.map((t) => (
          // A div, not a button, so an optional action (Undo) can nest a real
          // button inside without invalid button-in-button markup. Clicking the
          // toast body still dismisses it, as before.
          <div
            key={t.id}
            role="status"
            aria-live="polite"
            onClick={() => dismiss(t.id)}
            title="Dismiss"
            style={{
              pointerEvents: 'auto', cursor: 'pointer',
              background: COLORS.primary, color: COLORS.onPrimary,
              padding: '12px 18px', borderRadius: 999, fontSize: 13, fontWeight: 600,
              boxShadow: '0 12px 28px rgba(0,0,0,0.25)',
              display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'inherit',
            }}
          >
            <Icon name="check" size={16} />
            {t.message}
            {t.action && (
              <button
                type="button"
                onClick={(e) => {
                  // Run the action, then dismiss — without also triggering the
                  // body's dismiss-only handler twice.
                  e.stopPropagation()
                  t.action?.onAction()
                  dismiss(t.id)
                }}
                style={{
                  marginLeft: 4, border: 'none', background: 'transparent',
                  color: COLORS.onPrimary, cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: 13, fontWeight: 700, textDecoration: 'underline',
                  padding: 0,
                }}
              >
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
